import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API_URL } from '../../../config/api';
import { useAuth } from '../../../context/AuthContext';
import { useNotification } from '../../../context/NotificationContext';
import './Settings.css';

const Settings = () => {
  const { t } = useTranslation();
  const { user, updateUser } = useAuth();
  const { showError, showSuccess } = useNotification();
  const [isAvailable, setIsAvailable] = useState(false);
  const [vehicleInfo, setVehicleInfo] = useState('');
  const [locationEnabled, setLocationEnabled] = useState(false);
  const [lastLocationUpdate, setLastLocationUpdate] = useState(null);
  const [loading, setLoading] = useState(false);
  const initializedUserIdRef = useRef(null);

  // Initialize state from user and localStorage - only run once per user ID
  useEffect(() => {
    if (user) {
      const userId = user._id || user.id;
      
      // Only initialize if this is a new user or first time
      if (userId && initializedUserIdRef.current !== userId) {
        initializedUserIdRef.current = userId;
        
        // Load location tracking state from localStorage first (before setting other state)
        const savedLocationEnabled = localStorage.getItem(`locationTracking_${userId}`);
        if (savedLocationEnabled !== null) {
          setLocationEnabled(savedLocationEnabled === 'true');
        } else {
          // If no saved value, default to false
          setLocationEnabled(false);
        }
        
        // Then set other user properties
        setIsAvailable(user.isAvailable || false);
        setVehicleInfo(user.vehicleInfo || '');
        setLastLocationUpdate(user.currentLocation?.lastUpdated || null);
      } else if (userId && initializedUserIdRef.current === userId) {
        // Update other properties when user changes, but DON'T touch locationEnabled
        // locationEnabled is managed separately and should persist from localStorage
        setIsAvailable(user.isAvailable || false);
        setVehicleInfo(user.vehicleInfo || '');
        setLastLocationUpdate(user.currentLocation?.lastUpdated || null);
        // Do NOT update locationEnabled here - it's managed by the checkbox and localStorage effect
      }
    }
  }, [user?._id, user?.id, user?.isAvailable, user?.vehicleInfo, user?.currentLocation?.lastUpdated]); // Do NOT include locationEnabled to avoid circular updates
  
  // Separate effect to update location update time when it changes
  useEffect(() => {
    if (user?.currentLocation?.lastUpdated) {
      setLastLocationUpdate(new Date(user.currentLocation.lastUpdated));
    }
  }, [user?.currentLocation?.lastUpdated]);

  // Save location tracking state to localStorage whenever it changes
  useEffect(() => {
    if (user) {
      const userId = user._id || user.id;
      if (userId && locationEnabled !== undefined) {
        const currentValue = localStorage.getItem(`locationTracking_${userId}`);
        const newValue = locationEnabled.toString();
        
        // Only update if value actually changed to avoid infinite loops
        if (currentValue !== newValue) {
          localStorage.setItem(`locationTracking_${userId}`, newValue);
          // Dispatch custom event to notify AdminLayout
          window.dispatchEvent(new CustomEvent('locationTrackingChanged', { 
            detail: { enabled: locationEnabled, userId } 
          }));
        }
      }
    }
  }, [locationEnabled, user?._id, user?.id]);


  const handleAvailabilityToggle = async () => {
    try {
      setLoading(true);
      const response = await axios.patch(`${API_URL}/delivery-driver/availability`, {
        isAvailable: !isAvailable
      });
      const newAvailability = response.data.isAvailable;
      setIsAvailable(newAvailability);
      // Update user context with full user data
      if (updateUser) {
        updateUser({ ...user, isAvailable: newAvailability });
      }
    } catch (error) {
      console.error('Error updating availability:', error);
      showError(error.response?.data?.message || t('admin.errorUpdatingAvailability'));
    } finally {
      setLoading(false);
    }
  };

  const handleVehicleInfoUpdate = async () => {
    try {
      setLoading(true);
      const response = await axios.put(`${API_URL}/users/profile`, {
        vehicleInfo: vehicleInfo
      });
      // Update user context with new vehicle info
      if (updateUser && user) {
        updateUser({ ...user, vehicleInfo: response.data.vehicleInfo });
      }
      showSuccess(t('admin.vehicleInfoUpdated'));
    } catch (error) {
      console.error('Error updating vehicle info:', error);
      showError(error.response?.data?.message || t('admin.errorUpdatingVehicleInfo'));
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return t('admin.never');
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  return (
    <div className="driver-settings">
      <h2>{t('admin.driverSettings')}</h2>

      <div className="settings-section">
        <h3>{t('admin.availability')}</h3>
        <div className="setting-item">
          <label>
            <input
              type="checkbox"
              checked={isAvailable}
              onChange={handleAvailabilityToggle}
              disabled={loading}
            />
            <span>{t('admin.availableForDeliveries')}</span>
          </label>
        </div>
      </div>

      <div className="settings-section">
        <h3>{t('admin.vehicleInformation')}</h3>
        <div className="setting-item">
          <label>{t('admin.vehicleInfoLabel')}</label>
          <div className="vehicle-input-group">
            <input
              type="text"
              value={vehicleInfo}
              onChange={(e) => setVehicleInfo(e.target.value)}
              placeholder={t('admin.vehicleInfoPlaceholder')}
            />
            <button 
              className="btn btn-primary" 
              onClick={handleVehicleInfoUpdate}
              disabled={loading}
            >
              {loading ? t('admin.updating') : t('admin.update')}
            </button>
          </div>
        </div>
      </div>

      <div className="settings-section">
        <h3>{t('admin.locationTracking')}</h3>
        <div className="setting-item">
          <label>
            <input
              type="checkbox"
              checked={locationEnabled}
              onChange={(e) => {
                const newValue = e.target.checked;
                setLocationEnabled(newValue);
                // State will be saved to localStorage in useEffect
                // AdminLayout will pick up the change via event listener
              }}
            />
            <span>{t('admin.enableLocationTracking')}</span>
          </label>
          <p className="setting-description">
            {t('admin.locationTrackingDescription')}
          </p>
          {lastLocationUpdate && (
            <p className="location-status">
              {t('admin.lastLocationUpdate')}: {formatDate(lastLocationUpdate)}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Settings;
